import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchShipturtleOrderDetail, changeOrderStatus, downloadCombinedDocuments, downloadFileFromUrl, cancelShippingLabel, emailOrderToVendor, emailInvoiceToCustomer, exportOrderLogs } from '../api/api';
import { getCurrentUserRole } from '../services/shipturtleApi';
import { Copy } from 'lucide-react';
import { showToast } from '../utils/toast';
import { maskIfHidden, maskAddressIfHidden } from '../utils/customerMask';
import { getManifestState } from '../utils/helpers';
import {
  cancelledQuantity,
  openOrderFlags,
  paymentBadge,
  remainingLineValue,
  remainingOrderValue,
  remainingQuantity,
} from '../utils/orderLineState';
import OrderNotes from '../components/ShipturtleOrders/OrderNotes';
import { vendorSyncDisplay } from '../components/ShipturtleOrders/vendorSyncStatus';
import PrescriptionUploadModal, { openOrderPrescription } from '../components/ShipturtleOrders/PrescriptionUploadModal';
import './OrderDetails.css';
import '../components/ShipturtleOrders/ManageOrders.css';

function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const [downloadingDocuments, setDownloadingDocuments] = useState(false);
  // Keyed by shipment id: each leg in the history downloads its own merged PDF.
  const [exportingLogs, setExportingLogs] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);

  // Only admins may move an order back to "New Order"; the option is hidden for vendors
  // (and stays hidden while the role is still loading, since userRole is null until then).
  const isAdmin = userRole === 'admin';

  /**
   * Download one shipment's label + manifest as a single merged PDF.
   *
   * Addressed by shipment rather than by order: only one leg is ever the
   * order's active shipment, and the history exists precisely to reach the
   * others.
   */
  const toggleStatusDropdown = () => {
    setShowStatusDropdown(prev => !prev);
  };

  const toggleEmailDropdown = () => {
    setShowEmailDropdown(prev => !prev);
  };

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        setUserRole(roleData?.role);
      } catch (err) {
        console.error('Error fetching user role:', err);
      }
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    // Fetch order data from API
    const fetchOrderDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try to fetch from API first
        const response = await fetchShipturtleOrderDetail(orderId);

        if (response.success && response.order) {
          // Map API response to component format
          const mappedOrder = mapApiOrderToComponent(response.order);
          setOrder(mappedOrder);
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (apiError) {
        console.error('Error fetching order details:', apiError);
        setError('Failed to fetch order details. Please try again.');
        setOrder(null);
      }

      setLoading(false);
    };

    fetchOrderDetails();
  }, [orderId]);

  const handleStatusUpdate = async (newStatus) => {
    if (!order) return;
    setShowStatusDropdown(false);

    // Only admins may move an order back to "New Order" status from any bucket.
    if (!isAdmin && newStatus === 'new') {
      showToast({ type: 'error', message: 'You do not have permission to move orders back to New Order.' });
      return;
    }

    // Only admins may place an order in the Held bucket.
    if (!isAdmin && newStatus === 'held') {
      showToast({ type: 'error', message: 'You do not have permission to move orders to Held status.' });
      return;
    }

    // Only admins may cancel an order. Vendors have no cancellation rights at all.
    if (!isAdmin && newStatus === 'cancelled') {
      showToast({ type: 'error', message: 'You do not have permission to cancel orders.' });
      return;
    }

    // Ready to Ship needs a dispatchable shipment (generated label or manual
    // shipping details). The backend enforces this too and returns
    // error_code "shipment_required".
    if (newStatus === 'ready_to_ship' && !order.hasShipment) {
      showToast({ type: 'error', message: 'Label not generated for this order. Generate label or add manual shipping details before marking Ready to Ship.' });
      return;
    }

    setLoading(true);

    try {
      const response = await changeOrderStatus(
        order.id,
        newStatus,
        `Status changed from details view to ${newStatus}`
      );
      if (response && response.success) {
        const refreshed = await fetchShipturtleOrderDetail(order.id);
        if (refreshed && refreshed.success && refreshed.order) {
          setOrder(mapApiOrderToComponent(refreshed.order));
        }
        showToast({
          type: 'success',
          message: `Order status changed to ${newStatus}`,
        });
      } else if (response?.error_code === 'shipment_required') {
        // Stale detail data let the request through; the backend caught it.
        showToast({ type: 'error', message: response.error || 'Label not generated for this order. Generate label or add manual shipping details before marking Ready to Ship.' });
      } else {
        showToast({
          type: 'error',
          message: `Failed to change status: ${response?.message || response?.error || 'Unknown error'}`,
        });
      }
    } catch (err) {
      console.error('Error changing status:', err);
      showToast({
        type: 'error',
        message: `Error changing status: ${err.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async (action) => {
    toggleEmailDropdown();
    if (!order || !order.id) return;

    setLoading(true);
    try {
      let response;
      if (action === 'email_vendor') {
        response = await emailOrderToVendor(order.id);
      } else if (action === 'email_invoice') {
        response = await emailInvoiceToCustomer(order.id);
      }

      const isSuccess =
        response && response.success && ((response.total_failed ?? 0) === 0);

      if (isSuccess) {
        const successMessage =
          action === 'email_vendor'
            ? 'Order email sent to vendor successfully'
            : 'Invoice email sent to customer successfully';
        showToast({ type: 'success', message: successMessage });
      } else {
        const fallbackMessage =
          action === 'email_vendor'
            ? 'Failed to send email to vendor'
            : 'Failed to send invoice to customer';
        const reason =
          response?.errors?.[0]?.reason ||
          response?.error ||
          fallbackMessage;
        showToast({ type: 'error', message: reason });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      const fallbackMessage =
        action === 'email_vendor'
          ? 'Failed to send email to vendor'
          : 'Failed to send invoice to customer';
      showToast({
        type: 'error',
        message: `${fallbackMessage}: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (type) => {
    if (type === 'documents' || type === 'combined') {
      // Download combined label + manifest
      if (!order || !order.id) return;

      try {
        setDownloadingDocuments(true);
        const response = await downloadCombinedDocuments(order.id);
        if (response && response.success && response.combined_url) {
          // Download the file instead of opening in new tab
          const filename = response.filename || `order_${order.order_number}_combined_documents.pdf`;
          await downloadFileFromUrl(response.combined_url, filename);
        } else {
          showToast({ type: 'error', message: response?.message || 'Failed to download documents' });
        }
      } catch (error) {
        console.error('Error downloading combined documents:', error);
        showToast({ type: 'error', message: error.message || 'Error downloading documents' });
      } finally {
        setDownloadingDocuments(false);
      }
    } else {
      console.log('Download:', type);
      // TODO: Implement other download types if needed
    }
  };

  const handleCancelLabel = async () => {
    if (!order) return;

    if (!window.confirm('Are you sure you want to cancel this shipping label? This action cannot be undone.')) {
      return;
    }

    setShowStatusDropdown(false);
    setLoading(true);

    try {
      const response = await cancelShippingLabel({ orderIds: [order.id] });
      if (response && response.success) {
        alert('Shipping label cancelled successfully');
        // Refresh order details
        const refreshed = await fetchShipturtleOrderDetail(order.id);
        if (refreshed && refreshed.success && refreshed.order) {
          setOrder(mapApiOrderToComponent(refreshed.order));
        }
      } else {
        alert(`Failed to cancel shipping label: ${response?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error cancelling label:', error);
      alert(`Error cancelling shipping label: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportLogs = async () => {
    if (!order) return;

    setExportingLogs(true);
    try {
      await exportOrderLogs(order.id, order.orderNumber);
      showToast('Logs exported successfully', 'success');
    } catch (err) {
      console.error('Error exporting logs:', err);
      showToast('Failed to export logs', 'error');
    } finally {
      setExportingLogs(false);
    }
  };

  if (loading && !order) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        <div className="orderDetailsLoading">
          <div className="spinner"></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        <div className="orderDetailsError">
          <h3>Order Not Found</h3>
          <p>The order #{orderId} could not be found.</p>
          <button onClick={() => navigate(-1)}>Back to Orders</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{}}>
      <div className="orderDetailsContainer">
        {/* Header Section */}
        <div className="orderDetailsHeader">
          <div className="headerLeft">
            <button className="backButton" onClick={() => navigate(-1)}>
              ← Back
            </button>
            <div className="orderIdSection">
              <div className="orderIdRow">
                <h1 className="orderIdTitle">Order #{order.orderNumber}</h1>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(order.orderNumber);
                    showToast({ message: 'Order number copied to clipboard', toastOptions: { icon: '📋' } });
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px',
                    marginLeft: '8px',
                    color: '#666',
                    transition: 'color 0.2s',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#333'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#666'}
                  title="Copy Order Number"
                >
                  <Copy size={18} />
                </button>
                {order.channel === 'Shopify' && (
                  <span className="channelBadge shopify">🛍️ Shopify</span>
                )}
                {/* Sync state on the vendor's own Shopify store. The server
                    sends null when this viewer shouldn't see it (a vendor with
                    no Shopify connected), so absence is the whole rule here. */}
                {(() => {
                  const sync = vendorSyncDisplay(order.vendorShopifySync);
                  if (!sync) return null;
                  return (
                    <span
                      className={`syncStatusBadge ${sync.tone}`}
                      title={sync.error || undefined}
                    >
                      <span>{sync.label}</span>
                      {sync.orderId && (
                        <>
                          <span className="syncOrderId">#{sync.orderId}</span>
                          <button
                            type="button"
                            className="syncCopyBtn"
                            onClick={() => {
                              navigator.clipboard.writeText(sync.orderId);
                              showToast({
                                message: 'Vendor Shopify order ID copied to clipboard',
                                toastOptions: { icon: '📋' },
                              });
                            }}
                            title="Copy vendor Shopify order ID"
                          >
                            <Copy size={14} />
                          </button>
                        </>
                      )}
                    </span>
                  );
                })()}
              </div>
              <div className="orderMeta">
                <span className="orderDate">{order.orderDate} {order.orderTime}</span>
              </div>
            </div>
          </div>
          <div className="headerRight">
            <span className={`orderStatusBadge ${getStatusClass(order.orderStatus)}`}>
              {order.orderStatus}
            </span>
            <div className="statusActions">
              {/* The cancelled and RTO buckets only offer admin-only actions ("Move to New Orders"
                  and "Cancel Order"), so the dropdown would be empty for vendors — hide it entirely. */}
              {!(['cancelled', 'rto'].includes(order.rawStatus) && !isAdmin) && (
              <div className="statusButtonDropdown">
                <button
                  className="statusDropdownButton"
                  onClick={toggleStatusDropdown}
                  aria-expanded={showStatusDropdown}
                >
                  {order.rawStatus === 'new' && 'Confirm Order ▾'}
                  {order.rawStatus === 'confirmed' && 'Ready to Ship ▾'}
                  {order.rawStatus === 'ready_to_ship' && 'In Transit ▾'}
                  {order.rawStatus === 'in_transit' && 'Delivered ▾'}
                  {order.rawStatus === 'exceptions' && 'Delivered ▾'}
                  {order.rawStatus === 'delivered' && 'In Transit ▾'}
                  {order.rawStatus === 'held' && (isAdmin ? 'Move to New Orders ▾' : 'Change Status ▾')}
                  {order.rawStatus === 'rto' && (isAdmin ? 'Move to New Orders ▾' : 'Change Status ▾')}
                  {order.rawStatus === 'cancelled' && 'Move to New Orders ▾'}
                  {!['new', 'confirmed', 'ready_to_ship', 'in_transit', 'exceptions', 'delivered', 'held', 'rto', 'cancelled'].includes(order.rawStatus) && 'Change Status ▾'}
                </button>
                {showStatusDropdown && (
                  <>
                    <div className="dropdownOverlay" onClick={toggleStatusDropdown}></div>
                    <div className="statusDropdownMenu">
                      {/* For 'new' show Confirm / Ready to Ship / In Transit / Delivered / RTO / Hold / Cancel */}
                      {order.rawStatus === 'new' && (
                        <>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('confirmed'); }}>Confirm Order</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('ready_to_ship'); }}>Ready to Ship</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('in_transit'); }}>In Transit</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('delivered'); }}>Delivered</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('rto'); }}>RTO</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('exceptions'); }}>Exception</button>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('held'); }}>Hold</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel</button>
                          )}
                        </>
                      )}

                      {/* For 'confirmed' show Ready to Ship / Hold / Cancel */}
                      {order.rawStatus === 'confirmed' && (
                        <>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('ready_to_ship'); }}>Ready to Ship</button>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('held'); }}>Hold</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel</button>
                          )}
                        </>
                      )}

                      {/* For 'ready_to_ship' show In Transit / Delivered / Hold / Cancel */}
                      {order.rawStatus === 'ready_to_ship' && (
                        <>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('in_transit'); }}>In Transit</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('delivered'); }}>Delivered</button>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('held'); }}>Hold</button>
                          )}
                          {order.shippingLabel && (
                            <button onClick={() => { toggleStatusDropdown(); handleCancelLabel(); }} className="destructiveAction">Delete Label</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel</button>
                          )}
                        </>
                      )}

                      {/* For 'in_transit' show Delivered / Exception / RTO / Hold / Cancel */}
                      {order.rawStatus === 'in_transit' && (
                        <>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('delivered'); }}>Delivered</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('exceptions'); }}>Exception</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('rto'); }}>RTO</button>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('held'); }}>Hold</button>
                          )}
                          {order.shippingLabel && (
                            <button onClick={() => { toggleStatusDropdown(); handleCancelLabel(); }} className="destructiveAction">Delete Label</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel</button>
                          )}
                        </>
                      )}

                      {/* For 'exceptions' show Delivered / RTO / Retry / Hold / Cancel */}
                      {order.rawStatus === 'exceptions' && (
                        <>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('delivered'); }}>Delivered</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('rto'); }}>RTO</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('in_transit'); }}>Retry Delivery</button>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('held'); }}>Hold</button>
                          )}
                          {order.shippingLabel && (
                            <button onClick={() => { toggleStatusDropdown(); handleCancelLabel(); }} className="destructiveAction">Delete Label</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel</button>
                          )}
                        </>
                      )}

                      {/* For 'delivered' show In Transit / Confirmed / Hold / Cancel */}
                      {order.rawStatus === 'delivered' && (
                        <>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('in_transit'); }}>In Transit</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('confirmed'); }}>Confirmed</button>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('held'); }}>Hold</button>
                          )}
                          {order.shippingLabel && (
                            <button onClick={() => { toggleStatusDropdown(); handleCancelLabel(); }} className="destructiveAction">Delete Label</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel</button>
                          )}
                        </>
                      )}

                      {/* For 'held' show Move to New Orders / Confirmed / In Transit / Delivered / Cancel */}
                      {order.rawStatus === 'held' && (
                        <>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('new'); }}>Move to New Orders</button>
                          )}
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('confirmed'); }}>Ready to Ship</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('in_transit'); }}>In Transit</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('delivered'); }}>Delivered</button>
                          {order.shippingLabel && (
                            <button onClick={() => { toggleStatusDropdown(); handleCancelLabel(); }} className="destructiveAction">Delete Label</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel Order</button>
                          )}
                        </>
                      )}

                      {/* For 'rto' show Move to New Orders / Cancel */}
                      {order.rawStatus === 'rto' && (
                        <>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('new'); }}>Move to New Orders</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel Order</button>
                          )}
                        </>
                      )}

                      {/* For 'cancelled' show Move to New Orders (admins only; vendors cannot move back to New) */}
                      {order.rawStatus === 'cancelled' && isAdmin && (
                        <>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('new'); }}>Move to New Orders</button>
                        </>
                      )}

                      {/* Fallback: provide common options */}
                      {!['new', 'confirmed', 'ready_to_ship', 'in_transit', 'exceptions', 'delivered', 'held', 'rto', 'cancelled'].includes(order.rawStatus) && (
                        <>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('confirmed'); }}>Confirmed</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('ready_to_ship'); }}>Ready to Ship</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('in_transit'); }}>In Transit</button>
                          <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('delivered'); }}>Delivered</button>
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('held'); }}>Hold</button>
                          )}
                          {isAdmin && (
                            <button onClick={() => { toggleStatusDropdown(); handleStatusUpdate('cancelled'); }}>Cancel</button>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
              )}
            </div>
            {/* DISABLED: email options - re-enable in future */}
            {/* <div className="emailButtonDropdown">
              <button
                className="emailDropdownButton"
                onClick={toggleEmailDropdown}
              >
                Email ▾
              </button>
              {showEmailDropdown && (
                <>
                  <div className="dropdownOverlay" onClick={toggleEmailDropdown}></div>
                  <div className="emailDropdownMenu">
                    <button onClick={() => handleEmail('email_vendor')}>
                      Email Order to Vendor
                    </button>
                    <button onClick={() => handleEmail('email_invoice')}>
                      Email Invoice to Customer
                    </button>
                  </div>
                </>
              )}
            </div> */}
          </div>
        </div>

        {/* Order Information Bar */}
        <div className="orderInfoBar">
          <div className="infoBarItem">
            <label>Shipping Details:</label>
            <div className="infoValue">
              <span className="labelLink">{order.shipper} → {order.shippingLabel}</span>
            </div>
          </div>
          <div className="infoBarItem">
            <label>Documents:</label>
            <div className="infoValue">
              <button
                className={`downloadDocsButton${order.docsReady ? '' : ' isDisabled'}`}
                onClick={() => {
                  if (downloadingDocuments) return;
                  if (!order.docsReady) {
                    if (order.manifestState === 'stuck') {
                      showToast({
                        type: 'error',
                        message: 'Manifest is not available for this order yet. Use "Retry manifest" from the orders list to generate it.',
                      });
                    } else {
                      showToast({
                        type: 'info',
                        message: 'Documents are still being prepared. The manifest usually lands within a minute — this button will enable automatically.',
                      });
                    }
                    return;
                  }
                  handleDownload('combined');
                }}
                aria-disabled={downloadingDocuments || !order.docsReady}
                title={order.docsReady
                  ? 'Download Label & Manifest'
                  : order.manifestState === 'stuck'
                    ? 'Manifest is not available yet — use "Retry manifest" from the orders list.'
                    : 'Manifest is still being generated. This usually takes under a minute.'}
              >
                {downloadingDocuments ? (
                  <>
                    <span className="downloadSpinner"></span>
                    Downloading...
                  </>
                ) : order.docsReady ? (
                  '📄 Download Manifest & Label'
                ) : order.manifestState === 'stuck' ? (
                  '📄 Documents unavailable'
                ) : (
                  '📄 Preparing documents…'
                )}
              </button>
            </div>
          </div>
          {order.requiresPrescription && (
            <div className="infoBarItem">
              <label>Prescription:</label>
              <div className="infoValue" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  className={`downloadDocsButton ${order.prescription?.uploaded ? 'prescriptionUploadedBtn' : 'prescriptionRequiredBtn'}`}
                  onClick={() => setShowPrescriptionModal(true)}
                  title={
                    order.prescription?.uploaded
                      ? (order.prescription.kind === 'link' ? `Link (${order.prescription.link_host})` : order.prescription.original_filename)
                      : 'A prescription (PDF or link) is required before a label can be generated'
                  }
                >
                  {order.prescription?.uploaded
                    ? (order.prescription.kind === 'link' ? '✓ Link added' : '✓ Uploaded')
                    : '⚠ Required – Add'}
                </button>
                {isAdmin && order.prescription?.uploaded && (
                  <button
                    className="downloadDocsButton"
                    onClick={() => openOrderPrescription(order.id)}
                    title={order.prescription.kind === 'link' ? 'Open the prescription link' : 'Download the uploaded prescription'}
                  >
                    {order.prescription.kind === 'link' ? '🔗 Open' : '📄 Download'}
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="infoBarItem">
            <label>Payment Status:</label>
            <div className="infoValue">
              <span className={`paymentBadge ${order.paymentStatus.toLowerCase()}`}>
                {order.paymentStatus}
              </span>
            </div>
          </div>
          <div className="infoBarItem">
            <label>Gateway:</label>
            <div className="infoValue">
              <span>{order.paymentGateway}</span>
            </div>
          </div>
        </div>

        {/* Ops-only: these are Suspire's own payment / fulfilment alerts. */}
        {isAdmin && order.flags.length > 0 && (
          <div className="orderFlags">
            {order.flags.map((flag) => (
              <div key={flag.key} className={`orderFlag ${flag.tone}`}>
                <strong>{flag.title}.</strong> {flag.message}
              </div>
            ))}
          </div>
        )}

        {showPrescriptionModal && (
          <PrescriptionUploadModal
            order={order}
            // Admins can always upload. Vendors can make the first upload any time,
            // but cannot replace it once the order has a label or AWB (mirrors the server).
            canUpload={isAdmin || !order.prescription?.uploaded || !order.hasShipment}
            onClose={() => setShowPrescriptionModal(false)}
            onSaved={(prescription) => setOrder(prev => ({ ...prev, prescription }))}
          />
        )}

        {/* Tags Section - Proper positioning as a separate row in the info bar */}
        {order.tags && order.tags.length > 0 && (
          <div className="orderTagsSection">
            <div className="infoBarItem">
              <label>Tags:</label>
              <div className="infoValue tagsContainer" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                {order.tags.map((tag, index) => (
                  <span key={index} className="tagBadge">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Banner */}
        {order.rawStatus === 'cancelled' && (
          <div className="cancellationBanner" style={{ backgroundColor: '#fff5f5', color: '#c92a2a', padding: '16px', marginBottom: '24px', borderRadius: '8px', borderLeft: '4px solid #fa5252', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>🚫</span>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Order Cancelled</h4>
            </div>
            <div style={{ paddingLeft: '26px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                {order.cancellation_reason || 'No reason provided'}
              </div>
              {order.cancellationReasonNotes && (
                <div style={{ fontSize: '13px', color: '#868e96', marginTop: '4px' }}>
                  {order.cancellationReasonNotes}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Penalty Banner */}
        {order.penaltyPercentage > 0 && (
          <div style={{ backgroundColor: '#fff9db', color: '#e67700', padding: '16px', marginBottom: '24px', borderRadius: '8px', borderLeft: '4px solid #fcc419', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Penalty Applied</h4>
            </div>
            <div style={{ paddingLeft: '26px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                Penalty: {order.penaltyPercentage}% of order value
              </div>
              <div style={{ fontSize: '13px', color: '#868e96', marginTop: '4px' }}>
                Follow-up stage: {order.followUpStage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>
            </div>
          </div>
        )}

        <div className="orderDetailsBody">
          {/* Main Content */}
          <div className="orderDetailsMain">
            {/* Product Table */}
            <div className="productTableSection">
              <h3>Order Items</h3>
              <table className="productTable">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Product</th>
                    <th>HSN</th>
                    <th>Weight (grams)</th>
                    <th>Price (qty)</th>
                    <th>Discount</th>
                    <th>Suspire Discount</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {order.products.map((product, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <img src={product.image} alt={product.name} className="productImage" />
                      </td>
                      <td>
                        <div className="productInfo">
                          <div className="productSku">{product.sku}</div>
                          <div className="productName">{product.name}</div>
                        </div>
                      </td>
                      <td>
                        <div className="hsnCode">{product.hsn}</div>
                        <div className="taxRate">{product.taxRate}%</div>
                      </td>
                      <td>{product.weight}g</td>
                      <td>
                        <div>₹{product.price.toFixed(2)}</div>
                        <div className="quantity">(qty: {product.quantity})</div>
                        {product.cancelledQuantity > 0 && (
                          <div className="cancelledQuantity">
                            {product.remainingQuantity === 0
                              ? 'Cancelled on Shopify'
                              : `${product.cancelledQuantity} cancelled · ${product.remainingQuantity} to ship`}
                          </div>
                        )}
                      </td>
                      <td>₹{product.discount.toFixed(2)}</td>
                      <td>
                        ₹{product.suspireDiscount.toFixed(2)}
                        {product.suspireDiscountCodes?.length > 0 && (
                          <div className="suspireDiscountCode" style={{ fontSize: '0.75rem', color: '#666' }}>
                            {product.suspireDiscountCodes.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="productValue">₹{product.value.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Order Totals */}
            <div className="orderTotalsSection">
              <h3>Order Summary</h3>
              <div className="totalsGrid">
                <div className="totalRow">
                  <span>Total Excluding Tax:</span>
                  <span>₹{order.totals.excludingTax.toFixed(2)}</span>
                </div>
                <div className="totalRow">
                  <span>Total Tax:</span>
                  <span>₹{order.totals.tax.toFixed(2)}</span>
                </div>
                {order.totals.cgstTotal > 0 && (
                  <div className="totalRow" style={{ paddingLeft: '20px', fontSize: '0.85rem', color: '#666' }}>
                    <span>CGST{order.totals.cgstRate > 0 ? ` (${order.totals.cgstRate}%)` : ''}:</span>
                    <span>₹{order.totals.cgstTotal.toFixed(2)}</span>
                  </div>
                )}
                {order.totals.sgstTotal > 0 && (
                  <div className="totalRow" style={{ paddingLeft: '20px', fontSize: '0.85rem', color: '#666' }}>
                    <span>SGST{order.totals.sgstRate > 0 ? ` (${order.totals.sgstRate}%)` : ''}:</span>
                    <span>₹{order.totals.sgstTotal.toFixed(2)}</span>
                  </div>
                )}
                {order.totals.igstTotal > 0 && (
                  <div className="totalRow" style={{ paddingLeft: '20px', fontSize: '0.85rem', color: '#666' }}>
                    <span>IGST{order.totals.igstRate > 0 ? ` (${order.totals.igstRate}%)` : ''}:</span>
                    <span>₹{order.totals.igstTotal.toFixed(2)}</span>
                  </div>
                )}
                {order.totals.suspireDiscount > 0 && (
                  <div className="totalRow">
                    <span>
                      Suspire Discount
                      {order.totals.suspireDiscountCodes?.length > 0
                        ? ` (${order.totals.suspireDiscountCodes.join(', ')})`
                        : ''}:
                    </span>
                    <span className="discount">-₹{order.totals.suspireDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="totalRow highlighted">
                  <span>Total Including Tax:</span>
                  <span>₹{order.totals.includingTax.toFixed(2)}</span>
                </div>
                <div className="totalRow">
                  <span>Add Shipping:</span>
                  <span>₹{order.totals.shipping.toFixed(2)}</span>
                </div>
                <div className="totalRow">
                  <span>Tax on Shipping:</span>
                  <span>₹{order.totals.shippingTax.toFixed(2)}</span>
                </div>
                <div className="totalRow">
                  <span>Discount on Shipping:</span>
                  <span className="discount">-₹{order.totals.shippingDiscount.toFixed(2)}</span>
                </div>
                <div className="totalRow final">
                  <span>Total Order Value:</span>
                  <span>₹{order.totals.total.toFixed(2)}</span>
                </div>
                {order.remainingValue !== null && (
                  <div className="totalRow remainingValue">
                    <span>After items cancelled on Shopify:</span>
                    <span>₹{order.remainingValue.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tracking & Exceptions Timeline */}
            {(order.trackingEvents?.length > 0 || (order.rawStatus === 'exceptions' && order.exceptionInfo)) && (
              <div className="trackingTimelineSection" style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '1.2rem', color: '#1a1a1a', marginBottom: '16px', fontWeight: '600' }}>Tracking & Exceptions</h3>
                <div className="timelineContainer" style={{
                  background: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                  border: '1px solid #eef2f6'
                }}>
                  {/* Timeline Events */}
                  {order.trackingEvents?.length > 0 ? (
                    <div style={{ paddingLeft: '8px' }}>
                      {order.trackingEvents.map((event, index) => {
                        const isExceptionLike = ['exception', 'rto', 'held', 'lost', 'damaged'].includes(event.mapped_status);
                        const isLast = index === order.trackingEvents.length - 1;

                        return (
                          <div key={index} className="timelineEvent" style={{ display: 'flex', position: 'relative', marginBottom: isLast ? '0' : '20px' }}>
                            <div className="timelineDate" style={{
                              minWidth: '140px',
                              textAlign: 'right',
                              color: '#868e96',
                              fontSize: '13px',
                              fontWeight: '500',
                              paddingRight: '20px',
                              paddingTop: '3px'
                            }}>
                              {event.event_timestamp ? new Date(event.event_timestamp).toLocaleString('en-IN', {
                                day: '2-digit', month: 'short',
                                hour: '2-digit', minute: '2-digit'
                              }) : '—'}
                            </div>

                            <div style={{
                              position: 'relative',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              marginRight: '20px'
                            }}>
                              <div className="timelineDot" style={{
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                backgroundColor: isExceptionLike ? '#fa5252' : '#339af0',
                                border: `3px solid ${isExceptionLike ? '#ffe3e3' : '#d0ebff'}`,
                                zIndex: 1,
                                marginTop: '4px'
                              }}></div>
                              {!isLast && (
                                <div className="timelineLine" style={{
                                  position: 'absolute',
                                  top: '22px',
                                  bottom: '-28px',
                                  width: '2px',
                                  backgroundColor: '#e9ecef',
                                  zIndex: 0
                                }}></div>
                              )}
                            </div>

                            <div className="timelineContent" style={{ flex: 1, paddingBottom: isLast ? '0' : '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                <span style={{
                                  textTransform: 'capitalize',
                                  fontSize: '15px',
                                  fontWeight: '600',
                                  color: '#343a40'
                                }}>
                                  {(event.mapped_status || 'Unknown').replace(/_/g, ' ')}
                                </span>
                                {/* Prozo's own status for every event, e.g. "Ready To Ship (Pickup Failed)" */}
                                {event.reason_label && (
                                  <span style={(isExceptionLike || isFailureTrackingEvent(event)) ? {
                                    fontSize: '12px',
                                    padding: '3px 10px',
                                    backgroundColor: '#fff5f5',
                                    color: '#fa5252',
                                    border: '1px solid #ffe3e3',
                                    borderRadius: '16px',
                                    fontWeight: '600',
                                    boxShadow: '0 1px 2px rgba(250, 82, 82, 0.05)'
                                  } : {
                                    fontSize: '12px',
                                    padding: '3px 10px',
                                    backgroundColor: '#f1f3f5',
                                    color: '#495057',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '16px',
                                    fontWeight: '600'
                                  }}>
                                    {event.reason_label}
                                  </span>
                                )}
                              </div>
                              <div style={{ color: '#687380', fontSize: '14px', lineHeight: '1.4' }}>
                                {event.location && <span style={{ fontWeight: '500', color: '#495057' }}>{event.location}</span>}
                                {event.description && (
                                  <div style={{ marginTop: '4px', color: '#868e96' }}>
                                    <span style={{ fontWeight: '600', color: '#495057' }}>Remarks:</span>{' '}
                                    {event.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    !order.exceptionInfo && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#868e96', fontSize: '14px' }}>
                        No tracking events available yet.
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Order Logs */}
            <div className="orderLogsSection">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Order Logs</h3>
                {isAdmin && (
                  <button
                    className="downloadDocsButton"
                    onClick={handleExportLogs}
                    disabled={exportingLogs}
                  >
                    {exportingLogs ? (
                      <>
                        <span className="downloadSpinner"></span>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Export to Excel
                      </>
                    )}
                  </button>
                )}
              </div>
              <div className="logsTable">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>User</th>
                      <th>Change Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.logs.map((log, index) => (
                      <tr key={index}>
                        <td className="logTime">{log.timestamp}</td>
                        <td className="logUser">{log.user}</td>
                        <td className="logDescription">{log.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Order Notes & Mentions */}
            <OrderNotes orderId={order.id} />
          </div>

          {/* Right Sidebar */}
          <div className="orderDetailsSidebar">
            {/* Shipping Details */}
            <div className="sidebarCard">
              <h4>Shipping Details</h4>
              <div className="cardContent">
                <div className="detailRow">
                  <span className="label">Dimensions:</span>
                  <span className="value">{order.shipping.dimensions}</span>
                </div>
                <div className="detailRow">
                  <span className="label">Weight:</span>
                  <span className="value">{order.shipping.weight}</span>
                </div>
                <div className="detailRow">
                  <span className="label">Shipping Label:</span>
                  <span className="value linkValue">{order.shipper} → {order.shippingLabel}</span>
                </div>
                <div className="detailRow">
                  <span className="label">Status:</span>
                  <span className={`statusBadge ${getStatusClass(order.orderStatus)}`}>
                    {order.orderStatus}
                  </span>
                </div>
              </div>
            </div>

            {/* Cancellation Information (only for cancelled orders) */}
            {(order.rawStatus === 'cancelled' || order.orderStatus === 'Cancelled') && (
              <div className="sidebarCard" style={{ borderColor: '#c62828', borderWidth: '2px', borderStyle: 'solid' }}>
                <h4 style={{ color: '#c62828' }}>Cancellation Information</h4>
                <div className="cardContent">
                  <div className="detailRow">
                    <span className="label">Cancellation Reason:</span>
                    <span className="value" style={{ color: '#c62828', fontWeight: '500' }}>
                      {order.cancellation_reason}
                    </span>
                  </div>

                  {order.notes && (
                    <div style={{ marginTop: '12px' }}>
                      <div className="label" style={{ marginBottom: '6px', fontWeight: '500' }}>Cancellation Notes:</div>
                      <div style={{
                        background: '#f9f9f9',
                        padding: '10px',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        {order.notes}
                      </div>
                    </div>
                  )}

                  <div className="detailRow" style={{ marginTop: '12px' }}>
                    <span className="label">Cancelled At:</span>
                    <span className="value">{order.cancelledAt ? new Date(order.cancelledAt).toLocaleString('en-IN') : 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Vendor Details */}
            <div className="sidebarCard">
              <h4>Vendor Details</h4>
              <div className="cardContent">
                <div className="vendorName">{order.vendor.name}</div>
                {order.vendor.shopifyShopName && (
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                    Shop: {order.vendor.shopifyShopName}
                  </div>
                )}
                {order.vendor.primaryContactName && (
                  <div style={{ fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem', fontWeight: 500 }}>
                    {order.vendor.primaryContactName}
                  </div>
                )}
                <div className="vendorAddress">
                  {order.vendor.pickupAddress && <p>{order.vendor.pickupAddress}</p>}
                  {(order.vendor.city || order.vendor.state || order.vendor.pincode) && (
                    <p>{[order.vendor.city, order.vendor.state, order.vendor.pincode].filter(Boolean).join(', ')}</p>
                  )}
                  {order.vendor.phone && <p>📞 {order.vendor.phone}</p>}
                </div>
                <div className="vendorFinancials">
                  <div className="detailRow">
                    <span className="label">Vendor Earnings:</span>
                    <span className="value">₹{order.vendor.earnings.toFixed(2)}</span>
                  </div>
                  <div className="detailRow">
                    <span className="label">Payout Status:</span>
                    <span className={`payoutBadge ${order.vendor.payoutStatus.toLowerCase()}`}>
                      {order.vendor.payoutStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Details */}
            <div className="sidebarCard">
              <h4>Customer Details</h4>
              <div className="cardContent">
                <div className="customerName">{maskIfHidden(order.customer.name, order.vendor?.hide_customer_details, isAdmin)}</div>
                <div className="customerEmail">📧 {maskIfHidden(order.customer.email, order.vendor?.hide_customer_details, isAdmin)}</div>
                <div className="customerPhone">📞 {maskIfHidden(order.customer.phone, order.vendor?.hide_customer_details, isAdmin)}</div>

                <div className="addressBlock">
                  <h5>Shipping Address</h5>
                  <p>{maskAddressIfHidden(order.customer.shippingAddress, order.vendor?.hide_customer_details, isAdmin)}</p>
                </div>

                <div className="addressBlock">
                  <h5>Billing Address</h5>
                  <p>{maskAddressIfHidden(order.customer.billingAddress || 'Same as shipping address', order.vendor?.hide_customer_details, isAdmin)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to get status class
function getStatusClass(status) {
  const normalized = status.toLowerCase().replace(/ /g, '_');
  return normalized;
}

const PROZO_STATUS_LABELS = {
  1: 'Order Placed',
  2: 'Pickup Pending',
  3: 'Pickup Failed',
  4: 'Picked Up',
  5: 'In Transit',
  6: 'Out for Delivery',
  7: 'Not Serviceable',
  8: 'Delivered',
  9: 'Delivery Failed',
  10: 'Order Cancelled',
  11: 'RTO Requested',
  12: 'Return to Origin',
  13: 'RTO Out for Delivery',
  14: 'RTO Delivered',
  15: 'RTO Failed',
  16: 'Shipment Lost',
  17: 'Shipment Damaged',
  18: 'Shipment Delayed',
  19: 'Contact Customer Care',
  20: 'Shipment Held',
  21: 'RTO In Transit',
  25: 'Out for Pickup',
  26: 'RTO - Contact Customer Care',
  27: 'RTO - Shipment Delayed',
  28: 'AWB Registered',
  33: 'Manifested',
  101: 'Return Order Placed'
};

// Prozo failure statuses (Pickup Failed, Delivery Failed, RTO Failed) keep a red label on the
// timeline whatever bucket the event maps to
const PROZO_FAILURE_STATUS_CODES = new Set([3, 9, 15]);

function isFailureTrackingEvent(event = {}) {
  const match = /^prozo_status_(\d+)$/i.exec(String(event.reason_code || '').trim());
  const code = match ? Number(match[1]) : Number(event.raw_carrier_event?.orderStatusCode);
  return PROZO_FAILURE_STATUS_CODES.has(code);
}

function resolveTrackingReasonLabel(event = {}) {
  const rawEvent = event.raw_carrier_event || {};
  const reasonLabel = event.reason_label;
  const reasonCode = event.reason_code;
  const orderStatusCode = rawEvent.orderStatusCode;

  const candidates = [reasonLabel, reasonCode, orderStatusCode];
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') continue;
    const value = String(candidate).trim();
    if (!value) continue;

    if (/^prozo_status_\d+$/i.test(value)) {
      const code = Number(value.split('_').pop());
      return PROZO_STATUS_LABELS[code] || value.replace(/_/g, ' ');
    }

    if (/^\d+$/.test(value)) {
      const label = PROZO_STATUS_LABELS[Number(value)];
      if (label) return label;
    }

    return value;
  }

  return null;
}

function resolveTrackingDescription(event = {}) {
  if (event.description) return event.description;
  const rawEvent = event.raw_carrier_event || {};
  return (
    rawEvent.remark ||
    rawEvent.remarks ||
    rawEvent.orderSubStatusDescription ||
    rawEvent.orderStatusDescription ||
    rawEvent.statusDescription ||
    null
  );
}

// Map API order response to component format
function mapApiOrderToComponent(apiOrder) {
  // Map status names
  const statusMap = {
    'new': 'New Orders',
    'confirmed': 'Confirmed',
    'ready_to_ship': 'Ready to Ship',
    'in_transit': 'In Transit',
    'exceptions': 'Exceptions',
    'delivered': 'Delivered',
    'rto': 'RTO',
    'held': 'Held',
    'cancelled': 'Cancelled'
  };

  const orderDate = new Date(apiOrder.created_at);

  // Extract product information from line items
  const lineItems = apiOrder.line_items || [];

  // Calculate totals using backend data
  // total_price is the brand-listed (pre-discount) amount - the same figure
  // commission is calculated on (see backend Order.net_payable_amount for the
  // equivalent). netPayablePrice is what the customer actually pays/owes and
  // is what every total/collectible-amount display below must use.
  const totalPrice = parseFloat(apiOrder.total_price || 0);
  const totalDiscount = parseFloat(apiOrder.total_discount || 0);
  const suspireDiscount = parseFloat(apiOrder.suspire_discount_amount || 0);
  const suspireDiscountCodes = apiOrder.suspire_discount_codes || [];
  const netPayablePrice = Math.max(0, totalPrice - suspireDiscount);
  const shippingPrice = parseFloat(apiOrder.shipping_price || 0);
  const totalTax = parseFloat(apiOrder.tax_details?.total_tax_amount || 0);
  const totalExcludingTax = parseFloat(apiOrder.tax_details?.total_base_amount || 0);
  const cgstTotal = parseFloat(apiOrder.tax_details?.cgst_total || 0);
  const sgstTotal = parseFloat(apiOrder.tax_details?.sgst_total || 0);
  const igstTotal = parseFloat(apiOrder.tax_details?.igst_total || 0);

  // GST rates: read from any line item's tax_breakup (rates are uniform when present)
  const breakupSource = lineItems.find(it => it?.tax_details?.tax_breakup)?.tax_details?.tax_breakup || {};
  const cgstRate = parseFloat(breakupSource.cgst_rate || 0);
  const sgstRate = parseFloat(breakupSource.sgst_rate || 0);
  const igstRate = parseFloat(breakupSource.igst_rate || 0);
  
  // Calculate subtotal (net payable total - shipping)
  const subtotal = netPayablePrice - shippingPrice;

  return {
    id: apiOrder.id,
    orderNumber: apiOrder.order_number,
    orderDate: orderDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    orderTime: orderDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
    channel: 'Shopify', // Default since not in API
    orderStatus: statusMap[apiOrder.status] || 'New Orders',
    rawStatus: apiOrder.status || 'new',
    // Vendor-Shopify sync block, or null when this viewer doesn't see it.
    vendorShopifySync: apiOrder.vendor_shopify_sync || null,
    financial_status: apiOrder.financial_status, // keep raw
    // Shopify's current payment state (financial_status is creation-only).
    paymentStatus: paymentBadge(apiOrder),
    // Open order flags (payment alerts, stale label, vendor-store order).
    flags: openOrderFlags(apiOrder),
    // Value left after items were cancelled on Shopify; null when none were.
    remainingValue: remainingOrderValue(apiOrder),
    paymentGateway: apiOrder.payment_method || 'Cash on Delivery (COD)',
    shipper: apiOrder.shipment?.carrier?.name || 'Unknown Carrier',
    shippingLabel: apiOrder.shipment?.awb_number || '',
    // Keep the raw shipment so the Ready-to-Ship guard and the documents
    // download can check label_url / manifest_url directly.
    shipment: apiOrder.shipment || null,
    // Full AWB history. `shipment` above stays the current outbound label, so
    // nothing that already reads it changes; this adds every leg.
    hasShipment: !!(apiOrder.shipment && (apiOrder.shipment.label_url || apiOrder.shipment.awb_number)),
    docsReady: !!(apiOrder.shipment?.label_url && apiOrder.shipment?.manifest_url),
    // 'ready' | 'no-label' | 'preparing' | 'stuck' — see getManifestState. Lets the
    // documents button distinguish a manifest that's still coming from one that
    // never will, instead of showing "Preparing…" forever.
    manifestState: getManifestState(apiOrder.shipment),
    products: lineItems.map((item, index) => ({
      sku: item.sku || '',
      name: item.product_name || 'No Product',
      image: item.variant?.image?.url || item.variant?.product?.media?.[0]?.url || '',
      hsn: item.variant?.product?.shopify_id?.toString().slice(-8) || '00000000',
      taxRate: item.tax_details?.gst_rate != null ? parseFloat(item.tax_details.gst_rate).toFixed(0) : '0',
      weight: parseInt((parseFloat(item.variant?.weight_kg || 0) * 1000).toFixed(0)),
      price: parseFloat(item.price || 0),
      quantity: item.quantity || 1,
      // Units cancelled on Shopify before shipping; quantity stays as ordered.
      cancelledQuantity: cancelledQuantity(item),
      remainingQuantity: remainingQuantity(item),
      discount: parseFloat(item.total_discount || 0),
      suspireDiscount: parseFloat(item.suspire_discount_amount || 0),
      suspireDiscountCodes: item.suspire_discount_codes || [],
      // Value is the discounted (payable) value of the units still on the
      // order: price * remaining qty, minus the prorated Suspire discount.
      value: remainingLineValue(item)
    })),
    totals: {
      excludingTax: totalExcludingTax,
      tax: totalTax,
      cgstTotal,
      sgstTotal,
      igstTotal,
      cgstRate,
      sgstRate,
      igstRate,
      includingTax: subtotal,
      shipping: shippingPrice,
      shippingTax: 0,
      shippingDiscount: 0,
      suspireDiscount,
      suspireDiscountCodes,
      // netPayablePrice already nets out suspireDiscount (see `subtotal` above),
      // so the final total is subtotal + shipping, not totalPrice - it must
      // NOT subtract suspireDiscount a second time even though the "Suspire
      // Discount" row below is still shown for transparency on where the
      // reduction came from.
      total: subtotal + shippingPrice
    },
    shipping: {
      dimensions: `${apiOrder.shipment?.dimensions?.length_cm || 0} x ${apiOrder.shipment?.dimensions?.width_cm || 0} x ${apiOrder.shipment?.dimensions?.height_cm || 0} cm`,
      weight: `${(parseFloat(apiOrder.shipment?.weight_kg || 0) * 1000).toFixed(0)} gm`
    },
    exceptionInfo: apiOrder.shipment?.exception_info || null,
    trackingEvents: (apiOrder.shipment?.tracking_events || []).map((event) => {
      const rawEvent = event.raw_carrier_event || {};
      return {
        ...event,
        reason_label: resolveTrackingReasonLabel(event),
        description: resolveTrackingDescription(event),
        courier_partner: event.courier_partner || rawEvent.courierPartnerName || rawEvent.courier_partner || null
      };
    }),
    vendor: {
      name: apiOrder.vendor?.brand_name || apiOrder.vendor?.company_name || 'Unknown Vendor',
      shopifyShopName: apiOrder.vendor?.shopify_shop_name || '',
      primaryContactName: apiOrder.vendor?.primary_contact_name || '',
      pickupAddress: apiOrder.shipment?.pickup_address?.address?.address_line_1 || apiOrder.vendor?.billing_address?.address_line_1 || '',
      city: apiOrder.shipment?.pickup_address?.address?.city || apiOrder.vendor?.billing_address?.city || '',
      state: apiOrder.shipment?.pickup_address?.address?.state || apiOrder.vendor?.billing_address?.state || '',
      pincode: apiOrder.shipment?.pickup_address?.address?.pincode || apiOrder.vendor?.billing_address?.pincode || '',
      phone: apiOrder.shipment?.pickup_address?.address?.phone || apiOrder.vendor?.primary_contact_phone || apiOrder.vendor?.billing_address?.phone || '',
      earnings: parseFloat(apiOrder.total_price || 0) * 0.87,
      payoutStatus: apiOrder.financial_status === 'paid' ? 'PAID' : 'UNPAID',
      hide_customer_details: apiOrder.vendor?.hide_customer_details || false
    },
    customer: {
      name: apiOrder.customer?.full_name || 'Unknown Customer',
      email: apiOrder.customer?.email || '',
      phone: apiOrder.customer?.phone || '',
      shippingAddress: [
        apiOrder.shipping_address?.address_line_1,
        apiOrder.shipping_address?.address_line_2,
        apiOrder.shipping_address?.city,
        apiOrder.shipping_address?.state,
        apiOrder.shipping_address?.pincode,
      ].filter(part => part && part.trim() !== '').join(', ') || 'No Address',
      billingAddress: apiOrder.billing_address?.address_line_1 
        ? [
            apiOrder.billing_address.address_line_1,
            apiOrder.billing_address.address_line_2,
            apiOrder.billing_address.city,
            apiOrder.billing_address.state,
            apiOrder.billing_address.pincode,
          ].filter(part => part && part.trim() !== '').join(', ')
        : null
    },
    tags: apiOrder.tags?.tags || [],
    requiresPrescription: Boolean(apiOrder.requires_prescription),
    prescription: apiOrder.prescription || null,
    cancellation_reason: apiOrder.meta?.cancellation_reason || null,
    cancellationReasonNotes: apiOrder.meta?.cancellation_reason_notes || null,
    penaltyPercentage: parseFloat(apiOrder.penalty_percentage || 0),
    followUpStage: apiOrder.follow_up_stage || 'none',
    notes: apiOrder.notes || null,
    cancelledAt: apiOrder.meta?.cancelled_at || apiOrder.updated_at,
    logs: apiOrder.audit_logs?.map(log => {
      // Format the action description
      let description = '';
      const payload = log.payload_snapshot || {};

      // Any status transition (status_change_<old>_to_<new>, or the bulk variant)
      // renders generically from the payload, so new transitions don't fall
      // through to the raw default.
      if (log.action && (log.action.startsWith('status_change_') || log.action.startsWith('bulk_status_change_'))) {
        const isBulk = log.action.startsWith('bulk_status_change_');
        return {
          timestamp: new Date(log.created_at).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }),
          user: log.changed_by?.name || log.changed_by?.email || 'System',
          description: `Order status changed${isBulk ? ' (bulk)' : ''}: ${payload.old_status} → ${payload.new_status}${payload.notes ? ` (${payload.notes})` : ''}`
        };
      }

      switch (log.action) {
        case 'shopify_order_created':
        case 'shopify_order_created_grouped_by_vendor':
          description = `Order created from Shopify${payload.order_number ? ` (Order #${payload.order_number})` : ''}`;
          break;
        case 'label_generated': {
          // Prefer top-level channel_name (e.g. "dtdc-surface"); older audit logs
          // only have it nested under external_request. "WMS" is Prozo's default
          // placeholder, not a real courier.
          const rawChannel =
            payload.channel_name ||
            payload.external_request?.channel_name ||
            null;
          const usableChannel = rawChannel && rawChannel.toUpperCase() !== 'WMS' ? rawChannel : null;
          let carrier = payload.carrier_name || 'N/A';
          if (usableChannel) {
            carrier = usableChannel
              .split('-')
              .map(part => {
                const upper = part.toUpperCase();
                if (upper === 'DTDC' || upper === 'SDD/NDD') return upper;
                return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
              })
              .join(' ');
          } else if (payload.shipping_partner) {
            carrier = payload.shipping_partner;
          }
          description = `Shipping label generated - AWB: ${payload.awb_number}, Carrier: ${carrier}`;
          break;
        }
        case 'shipment_created_for_manual_shipping':
          description = `Shipment Created For Manual Shipping - AWB: ${payload.awb_number || 'N/A'}, Carrier: ${payload.shipping_partner || 'N/A'}`;
          break;
        case 'shipment_updated_for_manual_shipping':
          description = `Shipment Updated For Manual Shipping - AWB: ${payload.awb_number || 'N/A'}, Carrier: ${payload.shipping_partner || 'N/A'}`;
          break;
        case 'shipment_created_from_vendor_shopify_webhook':
          description = `Shipment created from vendor Shopify - AWB: ${payload.awb_number || 'N/A'}, Carrier: ${payload.shipping_partner || 'N/A'}`;
          break;
        case 'tracking_synced_to_suspire_shopify':
          description = `Tracking synced to Shopify${payload.customer_notified ? ' (customer notified)' : ''} - AWB: ${payload.awb_number || 'N/A'}, Carrier: ${payload.tracking_company || 'N/A'}`;
          break;
        case 'shopify_order_updated_from_admin_webhook': {
          const tags = Array.isArray(payload.new_tags?.tags) ? payload.new_tags.tags.join(', ') : '';
          description = `Order updated from Shopify${tags ? ` - Tags: ${tags}` : ''}${payload.cancel_reason ? ` - Cancellation reason: ${payload.cancel_reason}` : ''}`;
          break;
        }
        case 'shipping_label_cancelled':
          description = `Shipping label cancelled${payload.awb_number ? ` - AWB: ${payload.awb_number}` : ''}${payload.old_status && payload.new_status && payload.old_status !== payload.new_status ? ` (status: ${payload.old_status} → ${payload.new_status})` : ''}`;
          break;
        case 'tracking_link_updated':
          description = payload.new_tracking_link
            ? `Tracking link updated${payload.awb_number ? ` - AWB: ${payload.awb_number}` : ''}`
            : 'Tracking link cleared';
          break;
        case 'shipment_created_or_updated_for_label':
          description = `Shipment created for label - AWB: ${payload.awb_number || 'N/A'}, Carrier: ${payload.carrier || 'N/A'}`;
          break;
        case 'bulk_label_generated':
          description = `Shipping label generated (bulk) - AWB: ${payload.awb_number || 'N/A'}, Carrier: ${payload.carrier_name || 'N/A'}`;
          break;
        case 'manifest_generated':
        case 'manifest_generated_after_label':
        case 'manifest_generated_for_order':
          description = `Manifest generated${payload.awb_number ? ` - AWB: ${payload.awb_number}` : ''}${payload.pickup_location ? ` (Pickup: ${payload.pickup_location})` : ''}`;
          break;
        case 'bulk_manifest_generated':
          description = `Manifest generated (bulk)${payload.shipment_count ? ` - ${payload.shipment_count} shipments` : ''}${payload.pickup_location ? ` (Pickup: ${payload.pickup_location})` : ''}`;
          break;
        case 'order_status_updated_from_prozo':
          description = `Order status updated from carrier tracking: ${payload.old_status} → ${payload.new_status}${payload.is_rto ? ' (RTO)' : ''}${payload.awb_number ? ` - AWB: ${payload.awb_number}` : ''}`;
          break;
        // NOTE: The per-event "prozo_tracking_event" audit log was removed because
        // it duplicated the shipment tracking timeline (TrackingEvent), which is
        // already surfaced separately. Carrier scans now come only from tracking
        // events, not the audit log.
        case 'shipping_address_updated':
        case 'shipping_address_updated_in_place': {
          const changed = Array.isArray(payload.changed_fields) ? payload.changed_fields : [];
          const phoneOnly = payload.phone_changed && changed.length === 1;
          if (phoneOnly) {
            const oldPhone = payload.old_shipping_address?.phone || 'N/A';
            const newPhone = payload.new_shipping_address?.phone || 'N/A';
            description = `Ship-to phone updated: ${oldPhone} → ${newPhone}`;
          } else {
            const newAddr = payload.new_shipping_address || {};
            const addrParts = [newAddr.address_line_1, newAddr.city, newAddr.pincode].filter(Boolean);
            const addrSummary = addrParts.length ? ` (${addrParts.join(', ')})` : '';
            description = `Ship-to address updated${addrSummary}${payload.phone_changed ? ' (phone changed)' : ''}`;
          }
          break;
        }
        case 'payment_status_update':
          description = `Payment status changed: ${payload.old_financial_status || 'N/A'} → ${payload.new_financial_status || 'N/A'}${payload.shopify_synced ? ' (synced to Shopify)' : ''}`;
          break;
        case 'pickup_address_replaced':
          description = 'Pickup address replaced';
          break;
        case 'shopify_order_cancelled':
          description = `Order cancelled from Shopify${payload.cancel_reason ? ` - Reason: ${payload.cancel_reason}` : ''}${payload.previous_status ? ` (was: ${payload.previous_status})` : ''}`;
          break;
        case 'order_cancelled':
          description = `Order cancelled - Reason: ${payload.reason || 'No reason provided'}`;
          break;
        case 'penalty_applied':
          description = `Penalty applied: ${payload.penalty_percentage || 0}%`;
          break;
        case 'followup_sent':
          description = `Follow-up sent: ${(payload.follow_up_stage || '').replace(/\b\w/g, l => l.toUpperCase())}${payload.note ? ` — ${payload.note}` : ''}`;
          break;
        default:
          // Handle any other action types
          description = log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          if (payload.notes) {
            description += ` - ${payload.notes}`;
          }
      }

      return {
        timestamp: new Date(log.created_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }),
        user: log.changed_by?.name || log.changed_by?.email || 'System',
        description: description
      };
    }).reverse() || [
        {
          timestamp: new Date(apiOrder.created_at).toLocaleString('en-IN'),
          user: 'System',
          description: 'Order created from Shopify'
        }
      ]
  };
}

export default OrderDetails;